import {
	GUEST_ONLINE_DETAILS_URL,
	PURCHASE_DETAIL_URL,
} from 'my-purchases/constants/endpoints'
import { ORDER_TYPES } from 'my-purchases/constants/orderTypes'
import { useFetch } from 'my-purchases/hooks/useFetch/useFetch'
import { useFormatPrice } from 'my-purchases/hooks/useFormatPrice/useFormatPrice'
import { useGuestEmail } from 'my-purchases/hooks/useGuestEmail/useGuestEmail'
import type { NonNormalizedPurchaseDetails } from 'my-purchases/types/purchaseDetail'
import { useMemo } from 'react'
import { useSession } from 'session/src/hooks/useSession'
import useSWRImmutable from 'swr/immutable'
import { url } from 'utils/url'

import { normalizeData } from './normalization'

interface UsePurchaseDetailsProps {
	type: string | null
	purchaseId?: string | null
	orderId?: string | null
	disabled?: boolean
}

export const usePurchaseDetails = ({
	type,
	purchaseId,
	orderId,
	disabled,
}: UsePurchaseDetailsProps) => {
	const { fetcher } = useFetch()
	const { isReady, logged } = useSession()
	const guestEmail = useGuestEmail()
	const { formatPrice } = useFormatPrice()

	const isGuestUser = !logged && guestEmail && orderId
	const isOrderTypeValid =
		type === ORDER_TYPES.ONLINE || type === ORDER_TYPES.STORE
	const isUserAuthValid = logged || isGuestUser
	const shouldFetch =
		!disabled && isReady && isOrderTypeValid && isUserAuthValid

	const { data, error, isLoading, mutate, isValidating } = useSWRImmutable<{
		resolveData: NonNormalizedPurchaseDetails
		status: number
	} | null>(
		shouldFetch
			? {
					url: url(
						orderId
							? `${GUEST_ONLINE_DETAILS_URL}/${orderId}`
							: `${PURCHASE_DETAIL_URL}/${type}/${purchaseId}`
					),
					...(isGuestUser && {
						extraHeaders: { 'x-guest-email': guestEmail },
					}),
				}
			: null,
		fetcher,
		{}
	)

	const purchase = useMemo(() => {
		const { resolveData } = data ?? {}
		return resolveData && normalizeData({ purchase: resolveData, formatPrice })
	}, [data])

	return {
		purchase,
		error,
		isLoading: isLoading || isValidating,
		isError: !!error,
		reload: mutate,
	}
}
